import React, { useCallback, useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { LottiePlayer as PlayerNoSSR } from '@components/common/LottiePlayer'
import buddyAnimation from '@/lotties/buddy.json'

// Minimal structural view of the lottie-web AnimationItem handed back by the
// player's `lottieRef`. Avoids a direct dependency on `lottie-web` (it is only
// a transitive dep via @lottiefiles/react-lottie-player).
type LottieControls = { play: () => void; pause: () => void }

// Behaviour state → Lottie playback config. The JSON ships as a single
// continuous loop with no named markers, so we differentiate states by
// tempo + subtle CSS treatment rather than segment names.
type BehaviourConfig = {
  autoplay: boolean
  loop: boolean
  speed: number
  opacity: number
  hueRotate: number
}

const IDLE: BehaviourConfig = {
  autoplay: true,
  loop: true,
  speed: 1,
  opacity: 1,
  hueRotate: 0,
}
const SLEEP: BehaviourConfig = {
  autoplay: true,
  loop: true,
  speed: 0.35,
  opacity: 0.82,
  hueRotate: -12,
}
const STATIC: BehaviourConfig = {
  autoplay: false,
  loop: false,
  speed: 1,
  opacity: 1,
  hueRotate: 0,
}

const zzzFloat = keyframes`
  0%   { opacity: 0; transform: translate(0, 0) scale(0.9); }
  20%  { opacity: 1; }
  100% { opacity: 0; transform: translate(3px, -6px) scale(1.1); }
`

const Wrapper = styled.div<{
  $size: number
  $opacity: number
  $hueRotate: number
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  line-height: 0;
  opacity: ${(p) => p.$opacity};
  ${(p) => (p.$hueRotate ? `filter: hue-rotate(${p.$hueRotate}deg);` : '')}
  transition: opacity 0.3s ease, filter 0.3s ease;

  /* The Lottie player is rendered into the next direct child (a div). Force
     it to fill the wrapper so the SVG inside picks up the centering set by
     preserveAspectRatio="xMidYMid meet". */
  > div {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  > div > svg {
    display: block;
    margin: auto;
  }
`

const Zzz = styled.span<{ $size: number }>`
  position: absolute;
  top: -4px;
  right: -${(p) => Math.round(p.$size * 0.1)}px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: ${(p) => Math.max(8, Math.round(p.$size * 0.22))}px;
  font-weight: 700;
  color: #4a2f1a;
  opacity: 0;
  pointer-events: none;
  animation: ${zzzFloat} 2.4s ease-out infinite;
`

export type AssistantBuddyProps = {
  size?: number
  title?: string
  /**
   * When true, slows the animation and shows a floating "Zzz".
   * Use for states where the architect has nothing to do (e.g. EmptyState).
   */
  sleep?: boolean
  /**
   * Freeze the animation on the first frame — useful at very small sizes
   * (header / launcher icons) where looping motion reads as flicker.
   */
  staticFrame?: boolean
}

export const AssistantBuddy: React.FC<AssistantBuddyProps> = ({
  size = 64,
  title = 'Assistant buddy',
  sleep = false,
  staticFrame = false,
}) => {
  const config: BehaviourConfig = staticFrame
    ? STATIC
    : sleep
      ? SLEEP
      : IDLE

  // A looping Lottie drives a perpetual requestAnimationFrame loop. Chrome
  // already throttles rAF for backgrounded tabs, but make it explicit so the
  // buddy never burns the main thread while the tab is hidden. Static buddies
  // never autoplay, so there is nothing to gate.
  const animRef = useRef<LottieControls | null>(null)
  const handleLottieRef = useCallback(
    (anim: LottieControls) => {
      animRef.current = anim
      if (config.autoplay && document.hidden) anim.pause()
    },
    [config.autoplay],
  )
  useEffect(() => {
    if (!config.autoplay) return
    const onVisibility = (): void => {
      const anim = animRef.current
      if (!anim) return
      if (document.hidden) anim.pause()
      else anim.play()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [config.autoplay])

  return (
    <Wrapper
      $size={size}
      $opacity={config.opacity}
      $hueRotate={config.hueRotate}
      data-cy="assistant--buddy"
      aria-label={title || undefined}
      role={title ? 'img' : 'presentation'}
    >
      <PlayerNoSSR
        autoplay={config.autoplay}
        loop={config.loop}
        speed={config.speed}
        keepLastFrame
        lottieRef={handleLottieRef}
        src={buddyAnimation}
        rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
        style={{ width: '100%', height: '100%' }}
      />
      {sleep && size >= 40 && <Zzz $size={size}>z</Zzz>}
    </Wrapper>
  )
}
